import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { X, Loader2, UserPlus, Wand2, Sparkles, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { LeadsStore } from '../../lib/leadsStore';
import { cn } from '../../lib/utils';
import { LEAD_SOURCES } from '../../lib/leadSources';

const SOURCE_OPTIONS = LEAD_SOURCES;

const PURPOSE_OPTIONS = [
  'Purchase', 'Refinance',
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CreateLeadModal({ open, onClose }) {
  const navigate = useNavigate();
  const firstNameRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [buildFromNote, setBuildFromNote] = useState(true);
  const [form, setForm] = useState({
    firstName: '', lastName: '', phone: '', email: '',
    loanPurpose: 'Purchase', source: 'Website',
    notes: '',
  });

  useEffect(() => {
    if (open) {
      setForm({
        firstName: '', lastName: '', phone: '', email: '',
        loanPurpose: 'Purchase', source: 'Website',
        notes: '',
      });
      setBuildFromNote(true);
      setTimeout(() => firstNameRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const valid = form.firstName.trim() && form.lastName.trim() && (form.phone.trim() || form.email.trim());
  const noteHasContent = form.notes.trim().length > 20;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!valid || saving) return;
    setSaving(true);
    try {
      const fullName = `${form.firstName.trim()} ${form.lastName.trim()}`.trim();
      const payload = {
        name: fullName,
        phone: form.phone.trim(),
        email: form.email.trim(),
        city: '',
        loanIntent: form.loanPurpose,
        loanPurpose: form.loanPurpose,
        source: form.source,
        temp: 'hot',
        notes: form.notes.trim(),
      };
      const created = await LeadsStore.create(payload);
      if (!created?.id) throw new Error('Could not create lead');

      // Persist the Quick note to the lead's notes feed (always — whether or not we auto-build).
      const noteText = form.notes.trim();
      if (noteText) {
        try {
          await axios.post(`${BACKEND_URL}/api/notes`, { leadId: created.id, body: noteText });
        } catch (_) { /* non-fatal */ }
      }

      // If toggle is on and the note has substantive content, also auto-build the 1003.
      if (buildFromNote && noteHasContent) {
        toast.success(`Lead created — ${fullName}`, {
          description: 'Wingman is reading your note to build the application…',
          duration: 5000,
        });
        try {
          const r = await axios.post(`${BACKEND_URL}/api/wingman/build_application`, {
            lead_id: created.id,
            notes_text: noteText,
          });
          const fields = r.data.fields || {};
          const coBorrower = r.data.co_borrower || {};
          const leadPatch = r.data.lead_patch || {};
          // Persist fields that belong on the lead doc (name/phone/email/co_borrower/etc.)
          if (Object.keys(leadPatch).length) {
            await LeadsStore.update(created.id, leadPatch);
          }
          // Stash the application-form fields in localStorage keyed by lead id.
          // LeadProfile reads & clears on mount — this is necessary because the
          // wingman:fillApp event listener is only registered AFTER navigation,
          // so dispatching events here would be lost into the void.
          if (Object.keys(fields).length) {
            try {
              localStorage.setItem(`kf_pending_app_${created.id}`, JSON.stringify(fields));
            } catch (_) { /* quota — non-fatal */ }
          }
          const coCount = Object.keys(coBorrower).length;
          toast.success(
            `Built ${Object.keys(fields).length} application field${Object.keys(fields).length === 1 ? '' : 's'}` +
              (coCount ? ` + ${coCount} co-borrower field${coCount === 1 ? '' : 's'}` : ''),
            { description: r.data.summary || '', duration: 9000 },
          );
        } catch (err) {
          // The lead is already created — just inform that auto-build failed.
          toast(`Lead created. Couldn't auto-build the 1003 — open the lead and use 'Build from notes'.`);
        }
      } else {
        toast.success(`Lead created — ${fullName}`, {
          description: 'Opening their profile…',
          duration: 4000,
        });
      }

      onClose?.();
      navigate(`/leads/${created.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Could not create lead');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  return createPortal(
    (
    // Outer scroll wrapper — guarantees the dialog is reachable on short viewports
    // (centered when it fits, scroll-into-view when taller than the screen).
    // Rendered into document.body via Portal so transformed ancestors (e.g.
    // animate-fadeUp on <main>) can't trap our position:fixed inside them.
    <div
      className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-sm animate-fadeIn"
      onClick={onClose}
    >
      <div className="min-h-full flex items-center justify-center p-4 sm:p-6">
        <form
          data-testid="create-lead-modal"
          onClick={(e) => e.stopPropagation()}
          onSubmit={submit}
          className="relative w-full max-w-3xl rounded-2xl bg-white border hairline shadow-2xl rise-in flex flex-col my-auto"
        >
          {/* Header */}
          <div className="relative px-7 py-6 bg-neutral-900 text-white rounded-t-2xl overflow-hidden shrink-0">
            <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[hsl(var(--kf-gold))]/25 blur-2xl" />
            <div className="relative flex items-start justify-between gap-3">
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 rounded-xl bg-[hsl(var(--kf-gold))] text-neutral-900 flex items-center justify-center">
                  <UserPlus className="h-6 w-6" strokeWidth={2.5} />
                </div>
                <div>
                  <div className="display text-[22px] font-bold tracking-tight">Create lead</div>
                  <div className="text-[13px] text-white/65 mt-0.5">Drop a new prospect into your pipeline.</div>
                </div>
              </div>
              <button
                type="button"
                data-testid="create-lead-close"
                onClick={onClose}
                className="h-9 w-9 rounded-md hover:bg-white/10 flex items-center justify-center text-white/70"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="px-7 py-7 space-y-6">
            <div className="grid grid-cols-2 gap-5">
              <Field label="First name" required>
                <input
                  ref={firstNameRef}
                  data-testid="create-lead-first-name"
                  value={form.firstName}
                  onChange={(e) => set('firstName', e.target.value)}
                  className={inputCls}
                  placeholder="Megan"
                />
              </Field>
              <Field label="Last name" required>
                <input
                  data-testid="create-lead-last-name"
                  value={form.lastName}
                  onChange={(e) => set('lastName', e.target.value)}
                  className={inputCls}
                  placeholder="Doyle"
                />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-5">
              <Field label="Phone" hint="phone or email required">
                <input
                  data-testid="create-lead-phone"
                  value={form.phone}
                  onChange={(e) => {
                    // Auto-format to XXX-XXX-XXXX as the user types
                    let d = String(e.target.value).replace(/\D/g, '');
                    if (d.length === 11 && d.startsWith('1')) d = d.slice(1);
                    d = d.slice(0, 10);
                    const out = d.length <= 3 ? d
                      : d.length <= 6 ? `${d.slice(0,3)}-${d.slice(3)}`
                      : `${d.slice(0,3)}-${d.slice(3,6)}-${d.slice(6)}`;
                    set('phone', out);
                  }}
                  inputMode="tel"
                  maxLength={12}
                  className={cn(inputCls, 'mono')}
                  placeholder="XXX-XXX-XXXX"
                />
              </Field>
              <Field label="Email">
                <input
                  data-testid="create-lead-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => set('email', e.target.value)}
                  className={inputCls}
                  placeholder="megan.doyle@mail.com"
                />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-5">
              <Field label="Loan Purpose">
                <SelectInput
                  testId="create-lead-purpose"
                  value={form.loanPurpose}
                  onChange={(v) => set('loanPurpose', v)}
                  options={PURPOSE_OPTIONS}
                />
              </Field>
              <Field label="Source">
                <SelectInput
                  testId="create-lead-source"
                  value={form.source}
                  onChange={(v) => set('source', v)}
                  options={SOURCE_OPTIONS}
                />
              </Field>
            </div>

            {/* Notes + Build from notes toggle */}
            <div className="space-y-3">
              <div className="flex items-end justify-between gap-3">
                <div>
                  <div className="text-[13px] font-bold text-neutral-900">Quick note</div>
                  <div className="text-[11.5px] text-neutral-500 mt-0.5">
                    Anything you remember from the call — Wingman can turn it into a full 1003.
                  </div>
                </div>
                <span className="mono text-[10.5px] text-neutral-400 shrink-0">{form.notes.length} chars</span>
              </div>
              <textarea
                data-testid="create-lead-notes"
                value={form.notes}
                onChange={(e) => set('notes', e.target.value)}
                rows={5}
                className={cn(inputCls, 'h-auto py-3 leading-relaxed')}
                placeholder={`Met Sarah at the open house — Senior PM at Tesla, 5y, $9500/mo base + $750 bonus, credit 742. Looking at $720k home in Austin, 25% down. Spouse David, Staff Engineer at Dell, $8500/mo, credit 728.`}
              />

              {/* Build-from-notes toggle */}
              <button
                type="button"
                data-testid="create-lead-build-toggle"
                onClick={() => setBuildFromNote((v) => !v)}
                className={cn(
                  'w-full flex items-center gap-3 rounded-xl border px-4 py-3 transition text-left',
                  buildFromNote
                    ? 'bg-gradient-to-r from-[hsl(var(--kf-gold-soft))] to-white dark:from-[hsl(var(--kf-gold))]/15 dark:to-neutral-900 border-[hsl(var(--kf-gold))]/60'
                    : 'bg-white dark:bg-neutral-900 hairline hover:border-neutral-400 dark:hover:border-neutral-600',
                )}
              >
                <span className={cn(
                  'relative inline-flex h-6 w-11 rounded-full transition shrink-0 ring-1',
                  buildFromNote
                    ? 'bg-[hsl(var(--kf-gold))] ring-[hsl(var(--kf-gold-deep))]/40 shadow-[0_0_0_3px_hsl(var(--kf-gold))/0.15]'
                    : 'bg-neutral-200 dark:bg-neutral-700 ring-neutral-300 dark:ring-neutral-600'
                )}>
                  <span className={cn(
                    'absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition',
                    buildFromNote ? 'left-[22px]' : 'left-0.5'
                  )} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <Wand2 className="h-3.5 w-3.5 text-[hsl(var(--kf-gold-deep))]" strokeWidth={2.5} />
                    <span className="text-[13px] font-bold text-neutral-900 dark:text-neutral-100">
                      Build the application from this note
                    </span>
                  </div>
                  <div className="text-[11.5px] text-neutral-600 dark:text-neutral-400 mt-0.5">
                    {buildFromNote
                      ? <>After the lead is created, Wingman extracts <span className="font-semibold text-neutral-800 dark:text-neutral-200">income, employment, credit, property, loan terms</span> and even a co-borrower if mentioned.</>
                      : 'Just create the lead — you can run Build-from-Notes later from the profile.'}
                  </div>
                </div>
                {buildFromNote && (
                  <Sparkles className="h-4 w-4 text-[hsl(var(--kf-gold-deep))] shrink-0" />
                )}
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="px-7 py-5 border-t hairline bg-neutral-50/40 rounded-b-2xl flex items-center justify-between shrink-0">
            <div className="text-[12px] text-neutral-500">
              Required: <span className="font-semibold text-neutral-700">name</span> + <span className="font-semibold text-neutral-700">phone or email</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="h-10 px-5 rounded-md border hairline bg-white text-[13px] font-semibold text-neutral-700 hover:border-neutral-400 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                data-testid="create-lead-submit"
                disabled={!valid || saving}
                className="h-10 px-6 rounded-md btn-ink text-[13px] font-bold flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Creating…</>
                ) : (
                  <>
                    {buildFromNote && noteHasContent ? <Wand2 className="h-4 w-4" strokeWidth={2.5} /> : <UserPlus className="h-4 w-4" strokeWidth={2.5} />}
                    {buildFromNote && noteHasContent ? 'Create + build' : 'Create lead'}
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
    ),
    document.body,
  );
}

const inputCls = 'w-full h-12 px-4 rounded-lg bg-white border hairline text-[14.5px] font-semibold text-neutral-900 placeholder:text-neutral-400 placeholder:font-normal focus:outline-none focus:border-neutral-900 transition';

function SelectInput({ testId, value, onChange, options }) {
  return (
    <div className="relative">
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-12 pl-4 pr-10 rounded-lg bg-white border hairline text-[14.5px] font-semibold text-neutral-900 focus:outline-none focus:border-neutral-900 transition appearance-none cursor-pointer"
      >
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-500" strokeWidth={2.2} />
    </div>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <label className="block">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[12.5px] font-semibold text-neutral-700">
          {label}{required && <span className="text-red-500 ml-0.5">*</span>}
        </span>
        {hint && <span className="text-[10.5px] text-neutral-400">{hint}</span>}
      </div>
      {children}
    </label>
  );
}
